import { useState, useEffect, useCallback, useMemo } from 'react';
import { mastersApi } from '@/features/masters/api';
import { Truck, TruckInsert, TruckUpdate } from '@/features/masters/types';

/**
 * トラックデータを管理するフック (FastAPI経由)
 */
export function useTrucks() {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // データを取得する関数
  const fetchTrucks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mastersApi.trucks.getAll();
      setTrucks(data);
    } catch (err: unknown) {
      console.error('Failed to fetch trucks:', err);
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : 'トラックデータの取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回読み込み
  useEffect(() => {
    fetchTrucks();
  }, [fetchTrucks]);

  const getEntityById = useCallback((id: string) =>
    trucks?.find(item => item.id === id), [trucks]);

  const validateEntity = useCallback(() => null, []);

  // トラック作成
  const addTruck = async (data: TruckInsert) => {
    try {
      const result = await mastersApi.trucks.create(data);
      await fetchTrucks(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error creating truck:', error);
      throw error;
    }
  };

  // トラック更新
  const updateTruck = async (id: string, data: TruckUpdate) => {
    try {
      const result = await mastersApi.trucks.update(id, data);
      await fetchTrucks(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error updating truck:', error);
      throw error;
    }
  };

  // トラック削除
  const deleteTruck = async (id: string) => {
    try {
      await mastersApi.trucks.delete(id);
      await fetchTrucks(); // データを再取得
      return true;
    } catch (error) {
      console.error('Error deleting truck:', error);
      throw error;
    }
  };

  return useMemo(() => ({
    trucks: trucks || [],
    data: trucks || [],
    items: trucks || [],
    isLoading,
    error: error || null,
    isValidating: isLoading,
    refetch: fetchTrucks,
    refreshData: fetchTrucks,
    getEntityById,
    validateEntity,
    addTruck,
    updateTruck,
    deleteTruck,
  }), [
    trucks,
    isLoading,
    error,
    fetchTrucks,
    getEntityById,
    validateEntity,
    addTruck,
    updateTruck,
    deleteTruck,
  ]);
}
